import { useCallback, useEffect, useRef, useState } from "react";
import { WorldButton, WorldWordmark } from "./components/world/WorldPrimitives";
import "./output-world.css";

export const DEMO_PROMPT = "PERSONAL AI PROMPT — SARAH\n\nWHO I AM:\nI'm a warm, creative person who tends to be self-deprecating about my abilities. I light up when talking about the people I care about and the work that actually means something to me. I communicate casually and openly once I feel comfortable, but I need a minute to warm up. I don't respond well to being told what to do — I respond to curiosity and genuine interest. Skip the formality. Talk to me like a person.\n\nWHAT I'M BUILDING TOWARD:\nI want to find a way to bring my creativity outside of my classroom — whether that's through my art, writing about education, or something I haven't named yet. I've been doing meaningful work for a long time without much recognition and I'm starting to wonder what it would look like to do something just for me.\n\nWHAT I'M WORKING THROUGH:\nTime and energy are tight. I give a lot at work and I come home depleted. I also have some things weighing on me personally that make it hard to focus on myself. I don't need someone to fix it — I just need help thinking through things without it feeling like homework.\n\nHOW TO ACTUALLY HELP ME:\n- Help me brainstorm creative projects that don't require a lot of startup energy\n- Help me write — I have things to say but I get stuck getting started\n- Help me think through decisions without overwhelming me with options\n- If I bring up my classroom, go there with me — that's where I come alive\n- Help me organize my thoughts when things feel tangled\n\nTHE TONE THAT WORKS:\nWarm, a little funny, zero pressure. Don't give me lists unless I ask. One thing at a time. If I go quiet, don't push — just leave the door open. Treat me like someone who's capable of more than she thinks she is, but never say that out loud.";

const DEMO_RECIPIENT = "Sarah";
const DEMO_INTRODUCER = "John";
const DEMO_THEME = "warm";

const REACTION_OPTIONS = [
  { emoji: "😑", label: "Meh" },
  { emoji: "😁", label: "Good" },
  { emoji: "🤩", label: "Amazing" },
];

function ActionIcon({ kind }) {
  const paths = {
    copy: <><rect x="6" y="6" width="11" height="11" rx="2" /><path d="M14 6V4.8A1.8 1.8 0 0 0 12.2 3H4.8A1.8 1.8 0 0 0 3 4.8v7.4A1.8 1.8 0 0 0 4.8 14H6" /></>,
    download: <><path d="M10 3v9" /><path d="m6.5 8.5 3.5 3.5 3.5-3.5" /><path d="M4 14v2.2A1.8 1.8 0 0 0 5.8 18h8.4a1.8 1.8 0 0 0 1.8-1.8V14" /></>,
    email: <><rect x="3" y="5" width="14" height="10" rx="2" /><path d="m4 7 6 4 6-4" /></>,
    check: <path d="m4 10 4 4 8-9" />,
  };

  return (
    <svg className="tgc-output-action__icon" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {paths[kind]}
    </svg>
  );
}

export default function OutputPage({
  recipientName = DEMO_RECIPIENT,
  introducerName = DEMO_INTRODUCER,
  theme = DEMO_THEME,
  prompt = DEMO_PROMPT,
  giftLinkId = null,
  previewState = null,
  readOnly = false,
}) {
  const [copied, setCopied] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [showEmailInput, setShowEmailInput] = useState(previewState === "email");
  const [emailAttempted, setEmailAttempted] = useState(false);
  const [toast, setToast] = useState("");
  const [selectedEmoji, setSelectedEmoji] = useState(previewState === "feedback" || previewState === "feedback-success" ? "😁" : null);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(previewState === "feedback-success");
  const [showFeedback, setShowFeedback] = useState(previewState === "feedback" || previewState === "feedback-success");
  const initialSaveAttempted = useRef(false);
  const toastTimer = useRef(null);

  const saveToSupabase = useCallback(async ({ emoji, feedback, generatedPrompt }) => {
    if (!giftLinkId || readOnly) return;
    try {
      await fetch("/api/save-reaction", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-internal-token": process.env.REACT_APP_INTERNAL_API_SECRET,
        },
        body: JSON.stringify({
          giftLinkId,
          emoji: emoji || null,
          feedback: feedback || null,
          generatedPrompt: generatedPrompt || prompt,
        }),
      });
    } catch (err) {
      console.error("Failed to save to Supabase:", err);
    }
  }, [giftLinkId, prompt, readOnly]);

  const showToast = useCallback((message) => {
    setToast(message);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(""), 3000);
  }, []);

  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  useEffect(() => {
    if (initialSaveAttempted.current || !giftLinkId || !prompt || readOnly) return;
    initialSaveAttempted.current = true;
    saveToSupabase({ generatedPrompt: prompt });
  }, [giftLinkId, prompt, readOnly, saveToSupabase]);

  const handleCopy = () => {
    navigator.clipboard.writeText(prompt).then(() => {
      setCopied(true);
      showToast("Copied to clipboard!");
      window.setTimeout(() => setCopied(false), 3000);
    }).catch(() => {
      showToast("Couldn't copy. Select the prompt and copy it manually.");
    });
  };

  const handleDownload = () => {
    const blob = new Blob([prompt], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "my-ai-prompt.txt";
    anchor.click();
    URL.revokeObjectURL(url);
    showToast("Saved!");
  };

  const emailIsValid = /^\S+@\S+\.\S+$/.test(emailInput.trim());

  const handleEmail = () => {
    if (!showEmailInput) {
      setShowEmailInput(true);
      return;
    }

    setEmailAttempted(true);
    if (!emailIsValid) return;

    const subject = encodeURIComponent("Your Personal AI Prompt");
    const body = encodeURIComponent(
      "Hi " + recipientName + ",\n\nHere's the personal AI prompt from your Gift Conversation — a gift from " + introducerName + ".\n\nTo use it: copy the prompt below, go to any AI chat (Claude, ChatGPT, etc.), and paste it at the start of a new conversation. Then just talk to it like you talked to us.\n\n---\n\n" + prompt + "\n\n---\n\nEnjoy it. You earned it."
    );
    window.location.href = "mailto:" + emailInput + "?subject=" + subject + "&body=" + body;
    setEmailSent(true);
    showToast("Opening your email app...");
  };

  const handleReactionSelect = (emoji) => {
    setSelectedEmoji(emoji);
    setShowFeedback(true);
    setFeedbackSubmitted(false);
    saveToSupabase({ emoji, generatedPrompt: prompt });
  };

  const handleFeedbackSubmit = () => {
    if (!feedbackText.trim()) {
      setShowFeedback(false);
      return;
    }
    setFeedbackSubmitted(true);
    saveToSupabase({ emoji: selectedEmoji, feedback: feedbackText, generatedPrompt: prompt });
    showToast("Thanks for sharing that");
  };

  const isClean = theme === "clean";

  return (
    <main className="tgc-world-shell tgc-world-shell--experience tgc-output" aria-labelledby="tgc-output-title">
      <div className="tgc-output-backdrop" aria-hidden="true" />

      <header className="tgc-output-header">
        <WorldWordmark />
        <p>Your personal AI prompt</p>
      </header>

      <div className="tgc-output-frame">
        <section className="tgc-output-intro">
          <div className="tgc-output-threshold" aria-hidden="true"><span /></div>
          <div>
            <h1 id="tgc-output-title">
              {readOnly
                ? `Welcome back, ${recipientName}.`
                : (isClean ? `Yours to keep, ${recipientName}.` : `This is yours, ${recipientName}.`)}
            </h1>
            <p>
              {readOnly
                ? "Your completed keepsake is still inside its seven-day retention window."
                : isClean
                ? "Built from your conversation. Use it to hit the ground running with any AI."
                : "Built from everything you shared today. Use it to start any AI conversation — it already knows who you are."}
            </p>
          </div>
        </section>

        {readOnly && (
          <p className="tgc-output-restored" role="status">
            This is the original keepsake in read-only form. Save your own copy before the hosted version expires.
          </p>
        )}

        <div className="tgc-output-layout">
          <article className="tgc-output-keepsake" aria-labelledby="tgc-output-prompt-heading">
            <div className="tgc-output-keepsake__head">
              <p id="tgc-output-prompt-heading">Your personal AI prompt</p>
              <span>Yours to keep</span>
            </div>
            <pre className="tgc-output-prompt" tabIndex="0">{prompt}</pre>
          </article>

          <aside className="tgc-output-rail" aria-label="Save and use your prompt">
            <section className="tgc-output-guide">
              <h2>How to use this:</h2>
              <p>Copy the prompt above, go to any AI chat — Claude, ChatGPT, whatever you prefer — and paste it at the start of a new conversation. Then just talk. It'll know where to start.</p>
            </section>

            <section className="tgc-output-actions" aria-labelledby="tgc-output-save-heading">
              <h2 id="tgc-output-save-heading">Save your prompt</h2>
              <div className="tgc-output-action-grid">
                <WorldButton variant={copied ? "primary" : "secondary"} className="tgc-output-action" onClick={handleCopy}>
                  <ActionIcon kind={copied ? "check" : "copy"} />
                  {copied ? "Copied!" : "Copy to clipboard"}
                </WorldButton>
                <WorldButton variant="secondary" className="tgc-output-action" onClick={handleDownload}>
                  <ActionIcon kind="download" />
                  Save as a file
                </WorldButton>
              </div>

              <WorldButton variant="secondary" className="tgc-output-action tgc-output-action--email" onClick={handleEmail}>
                <ActionIcon kind="email" />
                Email it to myself
              </WorldButton>

              {showEmailInput && !emailSent && (
                <div className="tgc-output-email">
                  <label htmlFor="tgc-output-email">Email address</label>
                  <div className="tgc-output-email__row">
                    <input
                      id="tgc-output-email"
                      className="tgc-world-field"
                      type="email"
                      inputMode="email"
                      autoComplete="email"
                      value={emailInput}
                      onChange={(event) => {
                        setEmailInput(event.target.value);
                        if (emailAttempted) setEmailAttempted(false);
                      }}
                      onKeyDown={(event) => { if (event.key === "Enter") handleEmail(); }}
                      placeholder="your@email.com"
                      aria-invalid={emailAttempted && !emailIsValid}
                      aria-describedby={emailAttempted && !emailIsValid ? "tgc-output-email-error" : undefined}
                      autoFocus
                    />
                    <WorldButton variant="primary" onClick={handleEmail} disabled={!emailInput.trim()}>
                      Send
                    </WorldButton>
                  </div>
                  <p id="tgc-output-email-error" className="tgc-world-validation" role="alert">
                    {emailAttempted && !emailIsValid ? "Enter a valid email address." : ""}
                  </p>
                </div>
              )}
            </section>
          </aside>
        </div>

        {!readOnly && <section className="tgc-output-reflection" aria-labelledby="tgc-output-reaction-heading">
          <div>
            <h2 id="tgc-output-reaction-heading">How did this feel?</h2>
            <div className="tgc-output-reactions">
              {REACTION_OPTIONS.map(({ emoji, label }) => (
                <button
                  key={emoji}
                  className="tgc-output-reaction"
                  type="button"
                  onClick={() => handleReactionSelect(emoji)}
                  aria-label={`${label} reaction`}
                  aria-pressed={selectedEmoji === emoji}
                >
                  <span aria-hidden="true">{emoji}</span>
                  <strong>{label}</strong>
                </button>
              ))}
            </div>
          </div>

          {showFeedback && !feedbackSubmitted && (
            <div className="tgc-output-feedback">
              <label htmlFor="tgc-output-feedback">Anything you want to say about the experience? (optional)</label>
              <textarea
                id="tgc-output-feedback"
                className="tgc-world-field"
                value={feedbackText}
                onChange={(event) => setFeedbackText(event.target.value)}
                rows={4}
              />
              <WorldButton variant={feedbackText.trim() ? "primary" : "secondary"} onClick={handleFeedbackSubmit}>
                {feedbackText.trim() ? "Share" : "Skip"}
              </WorldButton>
            </div>
          )}

          {feedbackSubmitted && (
            <p className="tgc-world-state tgc-world-state--success" role="status">Thank you for that.</p>
          )}
        </section>}

        <footer className="tgc-output-closing">
          <div>
            <p>Know someone who could use this?</p>
            <a href="https://www.thegiftconversation.com" target="_blank" rel="noopener noreferrer">thegiftconversation.com</a>
          </div>
          <p>
            {isClean
              ? `${introducerName} set this up because they think you're worth it.`
              : `${introducerName} gave you this because they think you're worth it.`}
          </p>
        </footer>
      </div>

      {toast && <div className="tgc-output-toast" role="status" aria-live="polite">{toast}</div>}
    </main>
  );
}
